import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Share, PlusSquare, X, Download } from "lucide-react";

const KEY = "lif_install_dismissed_at";
const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
const recentlyDismissed = () => Date.now() - Number(localStorage.getItem(KEY) || 0) < 14 * 86400000;

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    let t;
    if (isIOS()) t = setTimeout(() => setShow(true), 6000);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); clearTimeout(t); };
  }, []);

  function dismiss() { localStorage.setItem(KEY, String(Date.now())); setShow(false); }
  async function install() {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice.catch(() => null);
    setDeferred(null);
    dismiss();
  }

  return (
    <AnimatePresence>
      {show && (
        <motion.div data-testid="install-prompt" initial={{ y: 120, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 30 }}
          className="fixed z-50 inset-x-3 bottom-[92px] lg:bottom-6 lg:left-auto lg:right-6 lg:w-[380px] rounded-2xl bg-[var(--ink)] text-white p-4 shadow-2xl flex gap-3 items-start">
          <img src="/logo192.png" alt="" className="w-11 h-11 rounded-xl bg-white/10 object-contain" />
          <div className="flex-1 min-w-0">
            <p className="font-serif text-lg leading-tight">Get the LockedInForever app</p>
            {deferred ? (
              <p className="text-xs text-white/60 mt-0.5">Install it on your home screen. Full screen, one tap away.</p>
            ) : (
              <p className="text-xs text-white/60 mt-0.5 flex flex-wrap items-center gap-1">Tap <Share className="w-3.5 h-3.5 inline text-[var(--gold)]" /> then <PlusSquare className="w-3.5 h-3.5 inline text-[var(--gold)]" /> "Add to Home Screen"</p>
            )}
            {deferred && (
              <button onClick={install} data-testid="install-prompt-install" className="mt-2 text-[11px] uppercase tracking-[0.16em] px-4 py-2 rounded-full bg-[var(--gold)] inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> Install</button>
            )}
          </div>
          <button onClick={dismiss} data-testid="install-prompt-dismiss" aria-label="Dismiss" className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
